import { getWindowDisplayId } from "@/desktop/use-window-role";
import { useCanvasStore } from "@/stores/canvas-store";
import { useProjectStore } from "@/stores/project-store";
import type {
  AgentAnnotationEmphasis,
  AgentAnnotationShape,
  AgentAnnotationTarget,
  AgentAnnotationTransition,
} from "@/lib/agent-annotation";
import {
  agentCursorAnnotationTargets,
  annotationPointerAnchor,
} from "@/lib/agent-annotation";

interface PresentPoint {
  x: number;
  y: number;
  displayId?: number | null;
}

interface PresentArea extends PresentPoint {
  width: number;
  height: number;
}

interface PresentAnnotationBase extends PresentArea {
  emphasis?: AgentAnnotationEmphasis;
  strokeWidth?: number;
  fillOpacity?: number;
  padding?: number;
  transition?: AgentAnnotationTransition;
  transitionDurationMs?: number;
  /** Presentation-only pen progress while a highlight is drawn. */
  drawProgress?: number;
}

export type PresentAnnotation =
  | (PresentAnnotationBase & {
      shape: Exclude<AgentAnnotationShape, "path" | "polyline">;
    })
  | (PresentAnnotationBase & {
      shape: "path" | "polyline";
      path: Array<{ x: number; y: number }>;
      arrowEnd?: boolean;
    });

export interface PresentCursorRequest {
  cursorPoint?: PresentPoint | null;
  annotation?: PresentAnnotation | null;
  /** Stable step identity so progressive frames update one retained mark. */
  annotationId?: string;
  /** Copy that remains anchored beside the completed annotation. */
  annotationCallout?: string;
  annotationCalloutRole?: "context" | "answer";
  /** Legacy rectangle input normalized into a rounded rectangle. */
  cursorArea?: PresentArea | null;
  cursorLabel?: string;
  agentId?: string | null;
  agentName?: string | null;
  agentColor?: string | null;
  projectId?: string | null;
  spaceId?: string | null;
  /** Monotonic playback identity. Defaults to wall time for legacy callers. */
  actionSeq?: number;
  exclusionFrameIds?: string[];
  annotationExclusionRects?: PresentArea[];
  frameExclusionRects?: PresentArea[];
  travelDurationMs?: number;
  completionToken?: string;
  /** Correlates narration that must remain stable until sequence delivery. */
  presentationDeliveryId?: string;
  /** Keeps retained marks together across multiple drawing calls in one turn. */
  executionId?: string;
}

const FALLBACK_CURSOR_ID = "denker-present-cursor";

function toExclusionTargets(
  rects: PresentArea[] | undefined,
  fallbackDisplayId: number,
) {
  return rects?.map((rect) => ({
    displayId: rect.displayId ?? fallbackDisplayId,
    globalX: rect.x,
    globalY: rect.y,
    width: rect.width,
    height: rect.height,
  }));
}

function updatePresentedFrameExclusions(
  request: PresentCursorRequest,
  agentId: string,
  spaceId: string,
): void {
  useCanvasStore.setState((state) => {
    const cursor = state.agentCursors.get(agentId);
    if (!cursor || cursor.spaceId !== spaceId) return state;
    const displayId =
      cursor.pointTarget?.displayId ?? getWindowDisplayId() ?? 0;
    const next = new Map(state.agentCursors);
    next.set(agentId, {
      ...cursor,
      ...(request.presentationDeliveryId
        ? {
            presentation: "point" as const,
            presentationDeliveryId: request.presentationDeliveryId,
          }
        : {}),
      exclusionFrameIds: request.exclusionFrameIds,
      frameExclusionTargets: toExclusionTargets(
        request.frameExclusionRects,
        displayId,
      ),
    });
    return { agentCursors: next };
  });
}

/** The callout is anchored where the cursor rests (`calloutAnchor`), so the
 * live cursor owns it and only one copy renders. */
function toAnnotationTarget(
  annotation: PresentAnnotation,
  displayId: number,
  actionSeq: number,
  calloutAnchor: { x: number; y: number },
  annotationId?: string,
  annotationCallout?: string,
  executionId?: string,
  calloutRole?: "context" | "answer",
): AgentAnnotationTarget {
  const common = {
    annotationId,
    executionId,
    displayId,
    globalX: annotation.x,
    globalY: annotation.y,
    width: annotation.width,
    height: annotation.height,
    seq: actionSeq,
    emphasis: annotation.emphasis,
    strokeWidth: annotation.strokeWidth,
    fillOpacity: annotation.fillOpacity,
    padding: annotation.padding,
    transition: annotation.transition,
    transitionDurationMs: annotation.transitionDurationMs,
    drawProgress: annotation.drawProgress,
  };
  const target: AgentAnnotationTarget =
    annotation.shape === "path" || annotation.shape === "polyline"
      ? {
          ...common,
          shape: annotation.shape,
          path: annotation.path,
          arrowEnd: annotation.arrowEnd,
        }
      : { ...common, shape: annotation.shape };
  const content = annotationCallout?.trim();
  if (!content) return target;
  return {
    ...target,
    callout: {
      content,
      globalX: calloutAnchor.x,
      globalY: calloutAnchor.y,
      ...(calloutRole ? { role: calloutRole } : {}),
    },
  };
}

/** Where the pen touches `annotation`; pass the mark's `annotationId` so the
 * pen starts exactly where its sketch ink starts. */
export function presentAnnotationPointer(
  annotation: PresentAnnotation,
  annotationId?: string,
): PresentPoint {
  const anchor = annotationPointerAnchor({
    annotationId,
    shape: annotation.shape,
    globalX: annotation.x,
    globalY: annotation.y,
    width: annotation.width,
    height: annotation.height,
    padding: annotation.padding,
    path:
      annotation.shape === "path" || annotation.shape === "polyline"
        ? annotation.path
        : undefined,
  });
  return { ...anchor, displayId: annotation.displayId };
}

export function presentCursor(request: PresentCursorRequest): boolean {
  const annotation: PresentAnnotation | null =
    request.annotation ??
    (request.cursorArea
      ? { ...request.cursorArea, shape: "rounded_rectangle" }
      : null);
  const point =
    request.cursorPoint ??
    (annotation
      ? request.annotationCallout
        ? presentAnnotationPointer(annotation, request.annotationId)
        : {
            x: annotation.x + annotation.width / 2,
            y: annotation.y + annotation.height / 2,
            displayId: annotation.displayId,
          }
      : null);
  const projectState = useProjectStore.getState();
  const projectId = request.projectId ?? projectState.currentProject?.id;
  const agentId = request.agentId?.trim() || FALLBACK_CURSOR_ID;
  const existing = useCanvasStore.getState().agentCursors.get(agentId);
  const spaceId =
    request.spaceId ?? projectState.currentSpace?.id ?? existing?.spaceId ?? "";
  if (!spaceId && !request.presentationDeliveryId) return false;

  if (!point) {
    updatePresentedFrameExclusions(request, agentId, spaceId);
    return true;
  }
  const label = request.cursorLabel?.trim();
  const content = label ?? "";
  const overlaysRuntimeStatus = !!request.presentationDeliveryId;
  const now = Date.now();
  const actionSeq = request.actionSeq ?? now;
  const displayId = point.displayId ?? getWindowDisplayId() ?? 0;
  useCanvasStore.getState().setAgentCursor({
    agentId,
    spaceId,
    projectId: projectId ?? undefined,
    name: request.agentName?.trim() || "Denker",
    color: request.agentColor?.trim() || "var(--color-accent)",
    targetFrameId: null,
    content: overlaysRuntimeStatus ? undefined : content,
    contentFull: overlaysRuntimeStatus ? undefined : content,
    pointTarget: {
      displayId,
      globalX: point.x,
      globalY: point.y,
      seq: actionSeq,
      travelDurationMs: request.travelDurationMs,
      completionToken: request.completionToken,
    },
    ...(annotation
      ? {
          annotationTarget: {
            ...toAnnotationTarget(
              annotation,
              displayId,
              actionSeq,
              point,
              request.annotationId,
              request.annotationCallout,
              request.executionId,
              request.annotationCalloutRole,
            ),
            drawnAt:
              (request.annotationId
                ? agentCursorAnnotationTargets(existing).find(
                    (target) => target.annotationId === request.annotationId,
                  )?.drawnAt
                : undefined) ?? now,
          },
        }
      : {}),
    annotationExclusionTargets: toExclusionTargets(
      request.annotationExclusionRects,
      displayId,
    ),
    ...(request.exclusionFrameIds !== undefined
      ? { exclusionFrameIds: request.exclusionFrameIds }
      : {}),
    ...(request.frameExclusionRects !== undefined
      ? {
          frameExclusionTargets: toExclusionTargets(
            request.frameExclusionRects,
            displayId,
          ),
        }
      : {}),
    presentation: "point",
    presentationDeliveryId: request.presentationDeliveryId,
    executionId: request.executionId,
    presentationNarration: overlaysRuntimeStatus ? content : undefined,
    actionSeq,
    lastSeen: new Date(now).toISOString(),
    origin: "chat",
  });
  return true;
}
